import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref, watch } from 'vue';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';
import SponsoredLabel from '@/features/sponsored-recommendations/components/SponsoredLabel.vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction } from '@/@types/graphql-types';
import type {
	SRClickTitleEvent,
	ListSponsoredAd,
	SRClickEvent,
	ListTitleSR,
} from '@/features/sponsored-recommendations/types';
import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { HorizontalTitleListItem, PosterClickEventPayload } from '@/components/timeline/types';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import type { Maybe } from '@/helpers/types';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { useElementSize } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationMultiTitleNewPage',
  props: {
    sponsoredAd: null,
    lazy: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    placement: null
  },
  emits: ["onPosterClick", "onTrackerClickout", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClick', data: SRClickTitleEvent<ListTitleSR>): void;
	(e: 'onTrackerClickout', data?: SRClickEvent<ListTitleSR>): void;
	(e: 'imageError', src: Maybe<string>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: ListSponsoredAd, lazy: boolean, fullWidth: boolean, additionalContexts: SnowplowContext[], placement?: ClickoutPlacement };





const { trackSraEvent } = useBackendTracking();

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed(() => sponsoredCampaign.value?.node);

const listName = computed(() => sponsoredCampaign.value?.promotionalTitle || campaignNode.value?.content.name);
const disclaimerText = computed(() => sponsoredCampaign.value?.disclaimerText);
const titles = computed(() => campaignNode.value?.titles.edges?.map(edge => edge.node) ?? []);
const landingPageURL = computed(() => sponsoredCampaign.value?.watchNowOffer.standardWebURL ?? '');

// Sponsored label looks stranded on full page due to space next to the last poster, so we keep it under last poster's edge always
const titleListRef = ref<InstanceType<typeof HorizontalTitleList> | null>(null);
const footerInset = ref(0);
const { width: titleListWidth } = useElementSize(() => (titleListRef.value?.$el as HTMLElement) ?? null);

watch([titleListWidth, titles], async () => {
	await nextTick();

	const root = titleListRef.value?.$el as HTMLElement | undefined;
	const items = root?.querySelector('.hidden-horizontal-scrollbar__items');
	const lastPoster = root?.querySelector('.horizontal-title-list__item:last-of-type');

	if (!items || !lastPoster || items.scrollWidth > items.clientWidth) {
		footerInset.value = 0;
		return;
	}

	footerInset.value = Math.max(0, items.getBoundingClientRect().right - lastPoster.getBoundingClientRect().right);
});

const posterOverrides = computed(() => {
	const overrides = sponsoredCampaign.value?.nodeOverrides ?? [];
	return Object.fromEntries(overrides.map(({ nodeId: id, promotionalImageUrl: url }) => [id, url]));
});

const transformedTitles = computed(() => {
	const overrides = sponsoredCampaign.value?.nodeOverrides ?? [];
	const offerUrlByTitleId = Object.fromEntries(
		overrides.map(({ nodeId: id, watchNowOffer }) => [id, watchNowOffer?.standardWebURL])
	);

	return titles.value.map(title => {
		if (offerUrlByTitleId[title.id]) {
			title.content.fullPath = offerUrlByTitleId[title.id] || title.content.fullPath;
		}

		return title;
	});
});

function transformPosterProps(posterProps: PosterGraphqlProps, title: HorizontalTitleListItem): PosterGraphqlProps {
	const posterUrl = posterOverrides.value?.[title.id];
	if (posterUrl == null) return posterProps;

	return {
		...posterProps,
		sponsoredRecImages: {
			imageUrl: posterUrl,
			sourceSets: [
				{
					srcSet: posterUrl?.replace(/\.\w+$/, '.avif'),
					imageProp: {
						extension: '.avif',
						type: 'image/avif',
					},
					sourceProp: {
						viewport: 'desktop',
					},
					media: null,
				},
			],
		},
		title: {
			...title,
			content: {
				...title.content,
				posterUrl,
			},
		},
	} as PosterGraphqlProps;
}

function onSRListPosterClicked({ title, index }: PosterClickEventPayload) {
	emits('onPosterClick', { title: title as ListTitleSR, position: index });

	trackSraEvent({
		action: SraEventAction.Clickout,
		sponsoredAd: () => props.sponsoredAd,
		jwEntityID: title.id,
		position: index,
	});
	emits('onTrackerClickout', { title: title as ListTitleSR, position: index });
}

function onSRListClick() {
	trackSraEvent({
		action: SraEventAction.Click,
		sponsoredAd: () => props.sponsoredAd,
	});

	if (landingPageURL.value) {
		trackSraEvent({
			action: SraEventAction.ListClickout,
			sponsoredAd: () => props.sponsoredAd,
		});
	}
	emits('onTrackerClickout', { title: titles.value[0], position: 0 });
}

return { __sfc: true,props, emits, trackSraEvent, sponsoredCampaign, campaignNode, listName, disclaimerText, titles, landingPageURL, titleListRef, footerInset, titleListWidth, posterOverrides, transformedTitles, transformPosterProps, onSRListPosterClicked, onSRListClick, PosterGraphql, HorizontalTitleList, SponsoredLabel }
}

})